import { type GroupChannel } from "@sendbird/chat/groupChannel";
import {
  type AdminMessage,
  type BaseMessage,
  type FileMessage,
  type UserMessage,
} from "@sendbird/chat/message";

import { HIDDEN_MESSAGE_TYPES, type MessageCustomType } from "../constants";
import {
  type AdminChatMessage,
  type ChatMessage,
  type FileChatMessage,
  type TextChatMessage,
  type WorkplaceUserMetadata,
} from "../types";

/**
 * Parse workplace user metadata from message data string
 */
function parseWorkplaceMetadata(dataString: string): WorkplaceUserMetadata | undefined {
  try {
    const data = JSON.parse(dataString) as WorkplaceUserMetadata;
    if (data.workplaceUserId && data.workplaceUserName) {
      return data;
    }
  } catch {
    // Invalid JSON or missing fields
  }
  return undefined;
}

/**
 * Channels in this app are 1:1, so an unread member count of zero means the
 * sole counter-party has read the message. Returns `undefined` when we have
 * no channel reference, so callers can distinguish "no data" from "not read".
 */
function computeReadByOther(
  message: BaseMessage,
  channel: GroupChannel | undefined
): boolean | undefined {
  if (!channel) {
    return undefined;
  }
  return channel.getUnreadMemberCount(message) === 0;
}

/**
 * Transform a Sendbird UserMessage to TextChatMessage
 */
function transformUserMessage(message: UserMessage, channel?: GroupChannel): TextChatMessage {
  const metadata = message.data ? parseWorkplaceMetadata(message.data) : undefined;
  const readByOther = computeReadByOther(message, channel);

  // Helper to check if parent message is a UserMessage with sender
  const isUserMessage = (baseMessage: BaseMessage): baseMessage is UserMessage =>
    baseMessage.isUserMessage();

  return {
    type: "text",
    messageId: message.messageId,
    createdAt: message.createdAt,
    channelUrl: message.channelUrl,
    customType: message.customType,
    senderId: message.sender?.userId,
    // Prefer the workplace user name so facilities can see which of their people
    // sent the message; fall back to the generic sender nickname otherwise.
    senderName: metadata?.workplaceUserName ?? message.sender?.nickname,
    message: message.message,
    metadata,
    parentMessage:
      message.parentMessage && isUserMessage(message.parentMessage)
        ? {
            messageId: message.parentMessage.messageId,
            message: message.parentMessage.message,
            senderName: message.parentMessage.sender?.nickname,
          }
        : undefined,
    ...(readByOther !== undefined && { readByOther }),
  };
}

/**
 * Transform a Sendbird FileMessage to FileChatMessage
 */
function transformFileMessage(message: FileMessage, channel?: GroupChannel): FileChatMessage {
  const metadata = message.data ? parseWorkplaceMetadata(message.data) : undefined;
  const readByOther = computeReadByOther(message, channel);

  return {
    type: "file",
    messageId: message.messageId,
    createdAt: message.createdAt,
    channelUrl: message.channelUrl,
    customType: message.customType,
    senderId: message.sender?.userId,
    senderName: metadata?.workplaceUserName ?? message.sender?.nickname,
    fileUrl: message.url,
    fileName: message.name,
    fileType: message.type,
    fileSize: message.size,
    thumbnailUrl: message.thumbnails?.[0]?.url,
    metadata,
    ...(readByOther !== undefined && { readByOther }),
  };
}

/**
 * Transform a Sendbird AdminMessage to AdminChatMessage
 */
function transformAdminMessage(message: AdminMessage): AdminChatMessage {
  return {
    type: "admin",
    messageId: message.messageId,
    createdAt: message.createdAt,
    channelUrl: message.channelUrl,
    customType: message.customType,
    message: message.message,
    data: message.data || undefined,
  };
}

/**
 * Transform a Sendbird BaseMessage to the appropriate ChatMessage type
 * Returns null for message types that should be hidden.
 *
 * When `channel` is provided, user/file messages get a `readByOther` flag
 * based on `channel.getUnreadMemberCount(message)`. Admin messages never
 * carry read state.
 */
export function transformSendbirdMessage(
  message: BaseMessage,
  channel?: GroupChannel
): ChatMessage | null {
  if (message.isUserMessage()) {
    return transformUserMessage(message, channel);
  }

  if (message.isFileMessage()) {
    return transformFileMessage(message, channel);
  }

  if (message.isAdminMessage()) {
    return transformAdminMessage(message);
  }

  // Unknown message type
  return null;
}

/**
 * Check if a message should be hidden from the workplace view
 */
export function shouldHideMessage(customType: string): boolean {
  return HIDDEN_MESSAGE_TYPES.includes(customType as MessageCustomType);
}

/**
 * Transform an array of Sendbird messages, filtering out null values and hidden messages
 */
export function transformSendbirdMessages(
  messages: BaseMessage[],
  channel?: GroupChannel
): ChatMessage[] {
  return messages
    .map((message) => transformSendbirdMessage(message, channel))
    .filter((message): message is ChatMessage => message !== null)
    .filter((message) => !shouldHideMessage(message.customType));
}
